
import { useRef, useEffect, useState, useCallback } from 'react';
import type { BlockRendererProps } from '@/lib/email-builder/block-registry';
import { parseBlockConfiguration } from '@/types/email-blocks';
import type { ButtonBlockConfiguration } from '@/types/block-configurations';
import { buildTextDecoration, buildFontStyle } from '@/lib/email-builder/text-style-utils';
import { useEmailBuilderStore } from '@/store/emailBuilderStore';
import { useDebouncedUpdate } from '@/hooks/useDebouncedUpdate';
import { stripHtmlTags } from '@/lib/html-sanitizer';

export function ButtonBlock({ block, isSelected, isHovered, onSelect, onHover }: BlockRendererProps) {
  const config = parseBlockConfiguration(block) as ButtonBlockConfiguration;

  const patchBlockConfigLocal = useEmailBuilderStore((s) => s.patchBlockConfigLocal);
  const persistBlockConfig = useEmailBuilderStore((s) => s.persistBlockConfig);
  const contentRef = useRef<HTMLSpanElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  // Text the DOM currently shows. A different config.text means the change came from elsewhere
  // (panel, undo, reload) and has to be written into the DOM.
  const shownTextRef = useRef<string | undefined>(undefined);

  // Typing updates the store at once (so the panel and undo see the current text) and the server
  // after a pause; the latest configuration is read when sending.
  const { debouncedUpdate, immediateUpdate } = useDebouncedUpdate({
    delay: 500,
    onUpdate: () => {
      persistBlockConfig(block.id).catch((error) => {
        console.error('Failed to save button text:', error);
      });
    },
  });

  // Show changes made outside this editor (and the initial content)
  useEffect(() => {
    if (!contentRef.current || config.text === shownTextRef.current) return;
    shownTextRef.current = config.text;
    const newContent = config.text || 'Button Text';
    if (contentRef.current.textContent !== newContent) {
      contentRef.current.textContent = newContent;
    }
  }, [config.text]);

  // Handle content changes (button text is plain text only)
  const handleInput = useCallback(() => {
    if (contentRef.current) {
      const plainText = stripHtmlTags(contentRef.current.textContent || '');
      shownTextRef.current = plainText;
      patchBlockConfigLocal(block.id, { text: plainText });
      debouncedUpdate(plainText);
    }
  }, [block.id, debouncedUpdate, patchBlockConfigLocal]);

  // Handle paste events - strip all HTML for buttons
  const handlePasteEvent = useCallback((e: React.ClipboardEvent) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    if (text && contentRef.current) {
      // Insert plain text only
      document.execCommand('insertText', false, text);
      handleInput();
    }
  }, [handleInput]);

  // Leaving the editor sends any edit still waiting
  const handleBlur = useCallback(() => {
    setIsEditing(false);
    immediateUpdate();
  }, [immediateUpdate]);

  // Handle double-click to enter edit mode
  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsEditing(true);
    // Focus the contentEditable element
    setTimeout(() => {
      if (contentRef.current) {
        contentRef.current.focus();
        // Select all text on double-click
        const range = document.createRange();
        range.selectNodeContents(contentRef.current);
        const selection = window.getSelection();
        if (selection) {
          selection.removeAllRanges();
          selection.addRange(range);
        }
      }
    }, 0);
  }, []);

  // Handle single-click - select block but don't edit
  const handleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect(block.id);
  }, [block.id, onSelect]);

  // Keyboard shortcuts
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    // Exit edit mode on Escape or Enter
    if (e.key === 'Escape' || e.key === 'Enter') {
      e.preventDefault();
      contentRef.current?.blur();
    }
    // Save on Ctrl/Cmd + S
    if ((e.ctrlKey || e.metaKey) && e.key === 's') {
      e.preventDefault();
      immediateUpdate();
    }
  }, [immediateUpdate]);

  return (
    <div
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      onMouseEnter={(e) => {
        e.stopPropagation();
        onHover(block.id);
      }}
      onMouseLeave={() => onHover(null)}
      className={`
        lw-block-button cursor-pointer rounded transition-all
        ${isSelected ? 'ring-2 ring-primary ring-offset-2' : ''}
        ${isHovered && !isSelected ? 'ring-1 ring-muted-foreground/50' : ''}
        ${isEditing ? 'ring-2 ring-blue-500 ring-offset-2' : ''}
      `}
      title={isEditing ? 'Editing - Press Escape or Enter to finish' : 'Double-click to edit button text'}
      style={{
        textAlign: config.alignment?.horizontal || 'center',
        // Mirrors mj-button's default container padding; config.padding is the
        // button's inner-padding (see ButtonBlockRenderer.cs), not this wrapper's.
        padding: '10px 25px',
      }}
    >
      <a
        href={config.href || '#'}
        className="inline-block transition-opacity hover:opacity-80"
        style={{
          backgroundColor: config.background?.color || '#007bff',
          color: config.typography?.color || '#ffffff',
          padding: `${config.padding?.top ?? 12}px ${config.padding?.right ?? 24}px ${config.padding?.bottom ?? 12}px ${config.padding?.left ?? 24}px`,
          borderRadius: `${config.border?.radius || 4}px`,
          fontSize: `${config.typography?.fontSize || 16}px`,
          fontFamily: config.typography?.fontFamily || 'Arial, sans-serif',
          fontWeight: config.typography?.fontWeight || 'bold',
          fontStyle: buildFontStyle(config.typography),
          textDecoration: buildTextDecoration(config.typography) || 'none',
          lineHeight: config.typography?.lineHeight || 1.5,
          textTransform: config.typography?.textTransform || 'none',
          letterSpacing: config.typography?.letterSpacing ? `${config.typography.letterSpacing}px` : 'normal',
          display: 'inline-block',
          border: config.border?.width ? `${config.border.width}px ${config.border.style} ${config.border.color}` : 'none',
          // Matches ButtonBlockRenderer.cs: full → 100%, otherwise minWidth becomes a fixed width
          width: config.width === 'full' ? '100%' : config.minWidth ? `${config.minWidth}px` : 'auto',
          maxWidth: config.maxWidth ? `${config.maxWidth}px` : undefined,
          textAlign: 'center',
          boxSizing: 'border-box',
        }}
        onClick={(e) => e.preventDefault()}
      >
        <span
          ref={contentRef}
          className={`outline-none ${isEditing ? 'cursor-text' : ''}`}
          contentEditable={isEditing}
          suppressContentEditableWarning
          onInput={handleInput}
          onPaste={handlePasteEvent}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
        />
      </a>
    </div>
  );
}
